import React from 'react';
import type Luminary from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';
import type { SafeLuminary, Scene } from '@lumen5/luminary';

import type { Drake } from 'dragula/index';
import {
    AssetContainerTypes,
    AssetTypes,
    SlideBackgroundModelTypeEnum,
    SourceEnum,
} from 'js/src/client';
import { Toast } from 'js/src/components/common/Toast';
import { EDITOR_TAB_OPTIONS, getCurrentEditorTab, setPendingChart } from 'js/src/data/app';
import { getArticleMedia } from 'js/src/data/article/article-selectors';
import { logDragFromBackgroundTab } from 'js/src/data/click-logs';
import type { StoredAssetContainer } from 'js/src/data/media';
import {
    getActiveSearchContextForCurrentTab,
    getCategoryMediaResult,
    getGenerationFromMediaUuid,
    getIsSimilarSearchContext,
    getMediaQueryResultByIndex,
    getMediaResult,
    getSimilarSearchResultByIndex,
    getVideoGenerationAssetData,
    isAnimatedSticker,
} from 'js/src/data/media';
import { setFocusedSceneInfo } from 'js/src/data/scenes';
import { getTemplateSceneDesignsByCategory } from 'js/src/data/templates';
import {
    readonlyAssetContainerModelIsAudio,
    readonlyAssetContainerModelIsBackground,
} from 'js/src/data/uploads';
import { addNarrationAndHandOffToChatOperation } from 'js/src/data/uploads/recorded-media-chat-handoff';
import { checkpointVideoOperation } from 'js/src/data/video-data';
import { addChartAssetToSceneOperation, getDefaultChartConfig } from 'js/src/libs/chart-asset-add';
import Flags from 'js/src/libs/flags';
import dragula from 'js/src/libs/dragula';
import type { RootState } from 'js/src/libs/RootState';
import SlideEventEmitter, { SLIDE_EVENTS } from 'js/src/libs/slide-event-emitter';
import uuid from 'js/src/libs/uuid';
import {
    addSceneFromMediaOperation,
    addSceneGroupFromVoiceoverAssetOperation,
    addTextElementToSceneOperation,
    applyDraggedVoiceoverToSceneOperation,
    applyFreepikStaticStickerOperation,
    buildFreepikIconSourceData,
    setIconAssetForSceneOperation,
    setIconSouceDataForSceneOperation,
    setMediaAssetForSceneOperation,
    SetMediaAssetForSceneOperationMethod,
    wouldSplitPrePackagedSceneSequence,
} from 'js/src/luminary';
import {
    toImmutableAudioAsset,
    toImmutableSlideBackground,
} from 'js/src/luminary/immutable-media-source-data';
import {
    createLuminaryOperation,
    type LuminaryOperationContext,
    useLuminaryOperations,
} from 'js/src/luminary/luminary-hooks';
import type { AddSceneGroupInfo } from 'js/src/luminary/luminary-types';

const getSceneCanvas = (scene: Scene): HTMLCanvasElement => {
    const div = document.querySelector(`[data-slideid="${scene.id}"]`);
    if (!div) {
        throw new Error(`No div found for scene ${scene.id}`);
    }
    const canvas = div.querySelector(
        'canvas[role="img"][aria-label="scene preview"]',
    ) as HTMLCanvasElement;
    if (!canvas) {
        throw new Error(`No canvas found for scene ${scene.id}`);
    }
    return canvas;
};

export enum MiscDragAssetTypes {
    STICKER = 'sticker', // Can be Icon or Media asset
    WAVEFORM = 'waveform',
    TEXT_ELEMENT = 'text-element',
    CHART = 'chart',
}
export type DragAssetTypes = AssetContainerTypes | MiscDragAssetTypes;

function getDragulaAssetTypeForElement(el: HTMLElement) {
    return el.dataset.dragulaAssetType;
}

function getAssetTypeForElement(el: HTMLElement) {
    return el.dataset.dragulaMediaAssetType;
}

function getDropTargetAttrForDraggingElement(el: HTMLElement) {
    const assetType = getDragulaAssetTypeForElement(el);
    if (
        assetType === AssetContainerTypes.BACKGROUND &&
        getAssetTypeForElement(el) === AssetTypes.VIDEO &&
        el.dataset.dragulaHasVoiceover === 'true'
    ) {
        return 'data-accepts-th-drag-and-drop';
    } else
        switch (assetType) {
            case AssetContainerTypes.BACKGROUND: {
                return 'data-accepts-media-drag-and-drop';
            }
            case AssetContainerTypes.ICON: {
                return 'data-accepts-icon-drag-and-drop';
            }
            case AssetContainerTypes.AUDIO: {
                return 'data-accepts-voiceover-drag-and-drop';
            }
            case MiscDragAssetTypes.STICKER: {
                return 'data-accepts-sticker-drag-and-drop';
            }
            case MiscDragAssetTypes.WAVEFORM: {
                return 'data-accepts-waveform-drag-and-drop';
            }
            case MiscDragAssetTypes.TEXT_ELEMENT: {
                return 'data-accepts-text-element-drag-and-drop';
            }
            case MiscDragAssetTypes.CHART: {
                return 'data-accepts-sticker-drag-and-drop';
            }
            default: {
                throw new Error(`invalid asset type: ${getDragulaAssetTypeForElement(el)}`);
            }
        }
}

function getIsIconForDraggingElement(el: HTMLElement) {
    return getAssetTypeForElement(el) === AssetTypes.ICON;
}

function getIsStickerForDraggingElement(el: HTMLElement) {
    const elAssetType = getDragulaAssetTypeForElement(el);
    return (
        elAssetType === MiscDragAssetTypes.STICKER || elAssetType === MiscDragAssetTypes.WAVEFORM
    );
}

function getIsAudioForDraggingElement(el: HTMLElement) {
    return getDragulaAssetTypeForElement(el) === AssetContainerTypes.AUDIO;
}

function getIsTextElementForDraggingElement(el: HTMLElement) {
    return getDragulaAssetTypeForElement(el) === MiscDragAssetTypes.TEXT_ELEMENT;
}

function getIsChartForDraggingElement(el: HTMLElement) {
    return getDragulaAssetTypeForElement(el) === MiscDragAssetTypes.CHART;
}

/**
 * a drag asset is an element that can be dragged
 */
function isValidDragAsset(el?: Element) {
    return (
        el !== undefined &&
        el.classList.contains('asset-draggable') &&
        (el.classList.contains('asset-holder') || el.classList.contains('voiceover-asset'))
    );
}

/**
 * a DnD container is a container that holds elements thatcan be dragged OR
 * a container that can be dropped into
 */
function isValidDnDContainer(contEl?: Element) {
    if (!contEl || !contEl.classList) {
        return false;
    }
    return (
        contEl.classList.contains('asset-container') || // these search results
        isValidDropContainer(contEl)
    );
}

/**
 * a drop container is a container that can be dropped onto
 */
function isValidDropContainer(contEl?: Element) {
    if (!contEl || !contEl.classList) {
        return false;
    }
    return (
        contEl.classList.contains('asset-target-container') || // asset drop targets
        contEl.classList.contains('slide-target-container') || // drop on scenes
        contEl.classList.contains('subscene-row') || // drop on scenes without media/icons
        contEl.classList.contains('scene-row') || // scene row (has data-slideid)
        contEl.classList.contains('post-slide-drop-target') // drop after scenes to create new scene
    );
}

// Calculate the position within luminary scene given position within viewport
function calculatePositionOnScene(
    luminary: SafeLuminary,
    sceneId: string,
    clientX: number,
    clientY: number,
) {
    const scene = luminary.getSceneById(sceneId);
    if (!scene) throw Error('No scene found');
    const sceneEl = getSceneCanvas(scene);
    const rect = sceneEl.getBoundingClientRect();
    const slidePosition = {
        x: clientX - rect.x,
        y: clientY - rect.y,
    };

    const relativeX = slidePosition.x / rect.width;
    const relativeY = slidePosition.y / rect.height;
    const luminaryX = relativeX * luminary.referenceResolution.width;
    const luminaryY = relativeY * luminary.referenceResolution.height;
    return {
        x: luminaryX,
        y: luminaryY,
    };
}

export enum DRAG_EVENTS {
    DRAGGING = 'DRAKE_DRAGGING',
    DRAGEND = 'DRAKE_DRAGEND',
}

const DRAGGING_EVENT = new CustomEvent(DRAG_EVENTS.DRAGGING);
const DRAGEND_EVENT = new CustomEvent(DRAG_EVENTS.DRAGEND);

const getAssetDataFromSearchContext = ({
    state,
    assetId,
    category = null,
    throwErrorIfNoSearchContext = true,
}: {
    state: RootState;
    assetId: string;
    throwErrorIfNoSearchContext?: boolean;
    category?: string | null;
}) => {
    const searchContext = getActiveSearchContextForCurrentTab(state);
    if (!searchContext) {
        if (throwErrorIfNoSearchContext) {
            throw new Error(
                `No search context found for current tab: ${getCurrentEditorTab(state)}`,
            );
        }
        return null;
    }
    if (category) {
        return getCategoryMediaResult(state, searchContext, category, assetId);
    }
    return getMediaResult(state, searchContext, assetId);
};

const onDropOperation = createLuminaryOperation(
    async (
        { getState, dispatch, call, luminary }: LuminaryOperationContext<Luminary>,
        el: Element,
        target: Element | null,
        source: Element | null,
        mousePosition?: { x: number; y: number },
    ) => {
        const systemTriggered = false;
        const parentActionGroupId = uuid();
        // remove the drag object & drag-over effect
        if (target) {
            target.classList.remove('drag-over');
        }
        el.remove();

        if (target === null || target === source) {
            // invalid, do nothing!
            return;
        }

        if (!(el instanceof HTMLElement)) {
            return;
        }

        const state = getState();

        const isSimilarSearchContext = getIsSimilarSearchContext(state);
        const activeTab = getCurrentEditorTab(state);

        // get asset
        const isIcon = getIsIconForDraggingElement(el);
        const isSticker = getIsStickerForDraggingElement(el);
        const isAudio = getIsAudioForDraggingElement(el);
        const isTextElement = getIsTextElementForDraggingElement(el);
        const isMediaQueryResult = el.getAttribute('data-is-media-query-result');
        const isArticleMedia = el.getAttribute('data-is-article-media');
        const assetId = el.getAttribute('data-dragula-media-id');
        const assetUrl = el.getAttribute('data-dragula-media-url');
        const mediaUuid = el.getAttribute('data-dragula-media-uuid');
        const resultIndex = el.getAttribute('data-dragula-media-index');
        const category = el.getAttribute('data-dragula-carousel-category');
        const isGenerativeMediaTabContext = activeTab === EDITOR_TAB_OPTIONS.GENERATIVE_MEDIA;
        let assetData: StoredAssetContainer | null | undefined = null;
        let isTemporaryGenerativeMedia = false;

        if (isGenerativeMediaTabContext) {
            if (assetId) {
                // Attempt to get the asset data from the search context. This determines if the asset has been saved or not
                assetData = getAssetDataFromSearchContext({
                    state,
                    assetId,
                    throwErrorIfNoSearchContext: false,
                });
            }
            // Temporary generative media have been generated but haven't been saved (favorited)
            isTemporaryGenerativeMedia = isGenerativeMediaTabContext && !assetData;
        }
        if (isSimilarSearchContext) {
            const isAIMedia =
                el.getAttribute('data-dragula-media-source') ===
                SourceEnum.SOURCE_AI_GENERATED.toString();
            assetData = getSimilarSearchResultByIndex(state, Number(resultIndex), isAIMedia);
        } else if (isTemporaryGenerativeMedia) {
            // Attempt to get the asset from the temporary image generation list
            const assetType = getAssetTypeForElement(el);
            const mediaType =
                assetType === AssetTypes.VIDEO
                    ? SlideBackgroundModelTypeEnum.VIDEO
                    : SlideBackgroundModelTypeEnum.IMAGE;
            if (mediaUuid) {
                const generationResult = getGenerationFromMediaUuid(state, mediaUuid, mediaType);
                if (generationResult) {
                    assetData = generationResult.asset;
                }
            }
            if (!assetData && assetId) {
                // Attempt to get the asset from the temporary video generation list
                assetData = getVideoGenerationAssetData(state, assetId);
            }
        } else if (isMediaQueryResult) {
            assetData = getMediaQueryResultByIndex(state, Number(resultIndex), Number(assetId));
        } else if (isArticleMedia && assetUrl) {
            assetData = getArticleMedia(state, assetUrl);
        } else if (assetId && category) {
            assetData = getAssetDataFromSearchContext({ state, assetId, category });
        } else if (assetId && !assetData) {
            assetData = getAssetDataFromSearchContext({ state, assetId });
        }

        const isChart = getIsChartForDraggingElement(el);
        if (isChart) {
            const closestRealTarget = target.closest(
                '[data-accepts-sticker-drag-and-drop="true"][data-slideid]',
            );
            const targetSceneId = closestRealTarget?.getAttribute('data-slideid');
            const chartType = el.getAttribute('data-dragula-chart-type');
            const configIndexAttr = el.getAttribute('data-dragula-chart-config-index');
            const configIndex = configIndexAttr !== null ? Number(configIndexAttr) : NaN;
            if (!targetSceneId || !chartType || Number.isNaN(configIndex) || !luminary) {
                return;
            }
            const config = getDefaultChartConfig(chartType, configIndex);
            if (!config) return;

            SlideEventEmitter.emit(SLIDE_EVENTS.GLOBAL_STOP_PREVIEW_PLAYBACK);

            const targetPosition = mousePosition
                ? calculatePositionOnScene(
                      luminary,
                      targetSceneId,
                      mousePosition.x,
                      mousePosition.y,
                  )
                : undefined;

            const result = call(
                addChartAssetToSceneOperation,
                targetSceneId,
                chartType,
                config,
                targetPosition,
            );
            if (result) {
                call(checkpointVideoOperation);
                dispatch(
                    setPendingChart({
                        sceneId: result.sceneId,
                        assetId: result.assetId,
                        chartType,
                    }),
                );
            }
            return;
        }

        if (isTextElement) {
            const closestRealTarget = target.closest(
                '[data-accepts-text-element-drag-and-drop="true"]',
            );
            const targetSceneId = closestRealTarget?.getAttribute('data-slideid');
            const sourceSceneId = el.getAttribute('data-dragula-source-scene-id');
            const sourceAssetId = el.getAttribute('data-dragula-source-asset-id');
            if (closestRealTarget && targetSceneId && sourceSceneId && sourceAssetId && luminary) {
                const targetPosition = mousePosition
                    ? calculatePositionOnScene(
                          luminary,
                          targetSceneId,
                          mousePosition.x,
                          mousePosition.y,
                      )
                    : null;
                call(
                    addTextElementToSceneOperation,
                    targetSceneId,
                    sourceSceneId,
                    sourceAssetId,
                    targetPosition,
                    false,
                    undefined,
                    true,
                );
            }
            return;
        }

        if (!assetData) {
            return;
        }

        if (
            target.classList.contains('subscene-row') ||
            target.classList.contains('asset-drop-target') ||
            target.classList.contains('slide-target-container') ||
            (isSticker && !target.classList.contains('post-slide-drop-target')) // stickers can be dropped on any scene
        ) {
            // dropped on a scene, which might mean we should replace that specific asset or we should
            // switch the scene design to one that supports media
            const target_scene_id = target.getAttribute('data-slideid');
            const target_asset_id = target.getAttribute('data-assetid');

            // stop playback (if playing)
            SlideEventEmitter.emit(SLIDE_EVENTS.GLOBAL_STOP_PREVIEW_PLAYBACK);
            if (target_scene_id) {
                let targetPosition;
                if (isSticker && mousePosition) {
                    targetPosition = calculatePositionOnScene(
                        luminary,
                        target_scene_id,
                        mousePosition.x,
                        mousePosition.y,
                    );
                }
                if (isIcon) {
                    call(
                        setIconAssetForSceneOperation,
                        target_scene_id,
                        assetData,
                        {
                            targetAssetId: target_asset_id,
                            targetPosition,
                            isDrag: true,
                        },
                        systemTriggered,
                        parentActionGroupId,
                    );
                } else if (isAudio) {
                    if (readonlyAssetContainerModelIsAudio(assetData)) {
                        const audioAssetData = assetData[AssetContainerTypes.AUDIO];
                        const audioUuid = audioAssetData?.uuid;
                        const targetSceneGroup = luminary.getSceneById(target_scene_id)?.sceneGroup;
                        // Toolbox users' voiceovers are handed to the toolbox chat,
                        // which builds the narration scenes itself — the legacy
                        // recommendation pipeline must not run for them. Dropping onto
                        // an existing scene group replaces that group's narration, so
                        // the hand-off swaps it in place rather than inserting.
                        if (
                            Flags.canUseToolbox() &&
                            audioAssetData?.has_voiceover &&
                            audioUuid &&
                            targetSceneGroup
                        ) {
                            const handoff = await call(addNarrationAndHandOffToChatOperation, {
                                assetUuid: audioUuid,
                                isTalkingHead: false,
                                targetSceneGroupIndex: targetSceneGroup.index,
                                replaceSceneGroupId: targetSceneGroup.id,
                            });
                            if (handoff !== 'notApplicable') {
                                if (handoff === 'failed') {
                                    Toast.error('Sorry, something went wrong with your voiceover.');
                                }
                                return;
                            }
                        }
                        call(
                            applyDraggedVoiceoverToSceneOperation,
                            target_scene_id,
                            toImmutableAudioAsset(audioAssetData),
                            systemTriggered,
                            parentActionGroupId,
                        );
                    }
                } else {
                    if (readonlyAssetContainerModelIsBackground(assetData)) {
                        const background = assetData[AssetContainerTypes.BACKGROUND];
                        const bgImage = background?.[SlideBackgroundModelTypeEnum.IMAGE];

                        if (bgImage && bgImage.source === SourceEnum.SOURCE_FREEPIK) {
                            if (isAnimatedSticker(bgImage.thumbnail_url)) {
                                call(
                                    setIconSouceDataForSceneOperation,
                                    target_scene_id,
                                    buildFreepikIconSourceData(
                                        bgImage.thumbnail_url,
                                        bgImage.source_id,
                                    ),
                                    {
                                        targetAssetId: target_asset_id,
                                        targetPosition,
                                    },
                                    systemTriggered,
                                    parentActionGroupId,
                                );
                            } else {
                                await call(
                                    applyFreepikStaticStickerOperation,
                                    target_scene_id,
                                    toImmutableSlideBackground(background),
                                    bgImage.source_id,
                                    {
                                        targetAssetId: target_asset_id,
                                        targetPosition,
                                        method: SetMediaAssetForSceneOperationMethod.DRAG,
                                    },
                                    systemTriggered,
                                    parentActionGroupId,
                                );
                            }
                        } else {
                            // Dropping a video with voiceover onto a talking-head
                            // scene runs the legacy recommendation pipeline; toolbox
                            // users' videos are handed to the toolbox chat instead.
                            // The user is swapping the recording on that scene, so the
                            // hand-off replaces the target group's narration in place.
                            const bgVideo = background?.[SlideBackgroundModelTypeEnum.VIDEO];
                            const videoUuid = bgVideo?.uuid;
                            const targetScene = luminary.getSceneById(target_scene_id);
                            const targetTalkingHeadSceneGroup =
                                targetScene?.sceneCategory === VideoData.CATEGORY_TALKING_HEAD
                                    ? targetScene.sceneGroup
                                    : null;
                            const shouldHandOffTalkingHead =
                                Flags.canUseToolbox() &&
                                !isSticker &&
                                !!bgVideo?.has_voiceover &&
                                !!videoUuid &&
                                !!targetTalkingHeadSceneGroup;
                            if (shouldHandOffTalkingHead) {
                                const handoff = await call(addNarrationAndHandOffToChatOperation, {
                                    assetUuid: videoUuid,
                                    isTalkingHead: true,
                                    targetSceneGroupIndex: targetTalkingHeadSceneGroup.index,
                                    replaceSceneGroupId: targetTalkingHeadSceneGroup.id,
                                });
                                if (handoff !== 'notApplicable') {
                                    if (handoff === 'failed') {
                                        Toast.error(
                                            'Sorry, something went wrong with your recording.',
                                        );
                                    }
                                    return;
                                }
                            }
                            call(setMediaAssetForSceneOperation, {
                                sceneId: target_scene_id,
                                mediaSourceData: toImmutableSlideBackground(background),
                                targetAssetId: target_asset_id,
                                method: SetMediaAssetForSceneOperationMethod.DRAG,
                                isRecommendedMedia: !!isMediaQueryResult,
                                isSticker,
                                targetPosition,
                                systemTriggered,
                                parentActionGroup: parentActionGroupId,
                            });
                        }
                    }
                }
            }
        } else if (target.classList.contains('post-slide-drop-target')) {
            // create new slide with media/audio
            const targetIndex = parseInt(target.getAttribute('data-index') || '');
            if (wouldSplitPrePackagedSceneSequence(luminary, targetIndex)) {
                return;
            }
            let newSceneDetails: AddSceneGroupInfo | undefined;
            if (isAudio) {
                if (!assetData || !readonlyAssetContainerModelIsAudio(assetData)) {
                    throw new Error('Audio asset is not audio container');
                }
                const audioAssetData = assetData[AssetContainerTypes.AUDIO];
                const audioUuid = audioAssetData?.uuid;
                // Toolbox users' voiceovers are handed to the toolbox chat,
                // which builds the narration scenes itself — the legacy
                // recommendation pipeline must not run for them.
                if (Flags.canUseToolbox() && audioAssetData?.has_voiceover && audioUuid) {
                    const handoff = await call(addNarrationAndHandOffToChatOperation, {
                        assetUuid: audioUuid,
                        isTalkingHead: false,
                        targetSceneGroupIndex: targetIndex,
                    });
                    if (handoff !== 'notApplicable') {
                        if (handoff === 'failed') {
                            Toast.error('Sorry, something went wrong with your voiceover.');
                        }
                        return;
                    }
                }
                newSceneDetails = await call(addSceneGroupFromVoiceoverAssetOperation, {
                    targetIndex,
                    audioAssetData: toImmutableAudioAsset(audioAssetData),
                    avatarBackgroundAssetData: null, // No avatar in drag-and-drop
                    deleteOtherScenes: false,
                    isGlobalVideo: true,
                    systemTriggered,
                    parentActionGroup: parentActionGroupId,
                });
            } else {
                if (assetData && readonlyAssetContainerModelIsBackground(assetData)) {
                    const background = assetData[AssetContainerTypes.BACKGROUND];
                    const bgVideo = background?.[SlideBackgroundModelTypeEnum.VIDEO];
                    const videoUuid = bgVideo?.uuid;
                    // A dropped video with voiceover becomes a talking-head scene
                    // group (when the template supports one), which runs the legacy
                    // recommendation pipeline; toolbox users' videos are handed to
                    // the toolbox chat instead.
                    const wouldBecomeTalkingHead =
                        bgVideo?.has_voiceover &&
                        getTemplateSceneDesignsByCategory(
                            getState(),
                            luminary.template.id,
                            VideoData.CATEGORY_TALKING_HEAD,
                        ).length > 0;
                    if (Flags.canUseToolbox() && wouldBecomeTalkingHead && videoUuid) {
                        const handoff = await call(addNarrationAndHandOffToChatOperation, {
                            assetUuid: videoUuid,
                            isTalkingHead: true,
                            targetSceneGroupIndex: targetIndex,
                        });
                        if (handoff !== 'notApplicable') {
                            if (handoff === 'failed') {
                                Toast.error('Sorry, something went wrong with your recording.');
                            }
                            return;
                        }
                    }
                    newSceneDetails = await call(
                        addSceneFromMediaOperation,
                        targetIndex,
                        toImmutableSlideBackground(background),
                        systemTriggered,
                        parentActionGroupId,
                    );
                }
            }
            newSceneDetails &&
                setFocusedSceneInfo({
                    sceneId: newSceneDetails.sceneId,
                    sceneGroupId: newSceneDetails.sceneGroupId,
                });
        }

        if (!isAudio && !isArticleMedia && !isGenerativeMediaTabContext) {
            logDragFromBackgroundTab(assetData, el);
        }
    },
);

// This type is used for manually exposing the functions & data, for testing purposes
type DrakeWithEmit = Drake & { emit: (event: string, ...args: Element[]) => void };
export type MediaDragAndDropPresentationExposed = {
    drake: DrakeWithEmit | null;
    drop: (
        el: Element,
        target: Element | null,
        source: Element | null,
        mousePosition?: { x: number; y: number },
    ) => Promise<void>;
    setupDragula: () => void;
    destroyDragula: () => void;
};

type OwnProps = {
    attachToGlobalForTesting?: (instance: MediaDragAndDropPresentationExposed) => void;
};

export const MediaDragAndDropPresentation = React.memo(function MediaDragAndDropPresentation({
    attachToGlobalForTesting,
}: OwnProps) {
    const drakeRef = React.useRef<Drake | null>(null);
    const mousePositionRef = React.useRef<{ x: number; y: number }>({ x: 0, y: 0 });
    const { onDrop } = useLuminaryOperations({
        onDropOperation,
    });

    const trackMousePosition = React.useCallback((e: MouseEvent) => {
        mousePositionRef.current.x = e.clientX;
        mousePositionRef.current.y = e.clientY;
    }, []);

    const setupDragula = React.useCallback(() => {
        const videoCreatorEl = document.querySelector('div.video-creator');
        // NOTE: the dragged elements can either be media tab or icons tab which have different drag targets & drop logic
        drakeRef.current = dragula({
            copy: true,
            mirrorContainer: videoCreatorEl ?? undefined, // to preserve formatting for dragging elements
            isContainer: isValidDnDContainer,
            accepts: function (
                el?: Element,
                target?: Element,
                _source?: Element,
                _sibling?: Element,
            ) {
                // Accept as long as asset and container are valid, handle specific asset compatibility later, on drop
                return isValidDragAsset(el) && isValidDropContainer(target);
            },
            moves: function (el?: Element, container?: Element, _handle?: Element) {
                return el !== undefined && el.classList.contains('asset-draggable'); // only assets should be draggable
            },
        });

        // handle drag effect
        drakeRef.current.on('shadow', (_el, container, _source) => {
            if (!(_el instanceof HTMLElement)) {
                return;
            }

            if (container.getAttribute(getDropTargetAttrForDraggingElement(_el)) !== 'true') {
                // Don't highlight on drag over if the container doesn't accept the drop
                return;
            }
            container.classList.add('drag-over');
            // make sure nte editable container and other elements are not blocking the drop
            const nteContainer = document.getElementById('luminary-nte-container');
            if (nteContainer) {
                nteContainer.classList.add('pe-none');
            }
            let containerSibling = container.nextElementSibling;
            while (containerSibling) {
                containerSibling.classList.add('pe-none');
                containerSibling = containerSibling.nextElementSibling;
            }
        });
        drakeRef.current.on('out', (_el, container, _source) => {
            container.classList.remove('drag-over');
            const nteContainer = document.getElementById('luminary-nte-container');
            if (nteContainer) {
                nteContainer.classList.remove('pe-none');
            }
            let containerSibling = container.nextElementSibling;
            while (containerSibling) {
                containerSibling.classList.remove('pe-none');
                containerSibling = containerSibling.nextElementSibling;
            }
        });
        drakeRef.current.on('cloned', (clone, _original, _type) => {
            // set the preload param for video to load the video in the dragging shadow
            const video = clone.querySelector('video');
            if (video) {
                video.setAttribute('preload', 'auto');
            }
            clone.classList.add('dragging');
        });

        // handle drop
        drakeRef.current.on('drop', (el, target, source) => {
            const mousePosition = {
                x: mousePositionRef.current.x,
                y: mousePositionRef.current.y,
            };
            onDrop(el, target, source, mousePosition);
        });

        // show drop targets while dragging
        drakeRef.current
            .on('drag', (el) => {
                el.dispatchEvent(DRAGGING_EVENT);
                window.addEventListener('mousemove', trackMousePosition);

                videoCreatorEl?.classList.add('is-dragging');

                if (!(el instanceof HTMLElement)) {
                    return;
                }

                const postSlideDropTargets = document.querySelectorAll('.post-slide-drop-target');
                postSlideDropTargets.forEach((target) => {
                    if (target.getAttribute(getDropTargetAttrForDraggingElement(el)) !== 'true') {
                        return;
                    }
                    target.classList.add('post-slide-drop-target-visible');
                });
                const assetDropTargets = document.querySelectorAll('.asset-target-container');
                assetDropTargets.forEach((target) => {
                    if (target.classList.contains('locked')) {
                        return;
                    }
                    if (target.getAttribute(getDropTargetAttrForDraggingElement(el)) !== 'true') {
                        return;
                    }
                    target.classList.add('asset-drop-target');
                });
            })
            .on('dragend', (el) => {
                el.dispatchEvent(DRAGEND_EVENT);
                window.removeEventListener('mousemove', trackMousePosition);

                videoCreatorEl?.classList.remove('is-dragging');

                if (!(el instanceof HTMLElement)) {
                    return;
                }

                const postSlideDropTargets = document.querySelectorAll('.post-slide-drop-target');
                postSlideDropTargets.forEach((target) => {
                    target.classList.remove('post-slide-drop-target-visible');
                });
                const assetDropTargets = document.querySelectorAll('.asset-target-container');
                assetDropTargets.forEach((target) => {
                    target.classList.remove('asset-drop-target');
                });
            });
    }, [onDrop, trackMousePosition]);

    const destroyDragula = React.useCallback(() => {
        if (drakeRef.current) {
            try {
                // need to wrap in try/catch because sometimes throws TypeErrors
                drakeRef.current.destroy();
            } catch (e) {
                // pass
            }
        }
    }, []);

    React.useEffect(() => {
        setupDragula();
        return () => {
            destroyDragula();
            window.removeEventListener('mousemove', trackMousePosition);
        };
    }, [setupDragula, destroyDragula, trackMousePosition]);

    React.useEffect(() => {
        if (attachToGlobalForTesting) {
            // For testing purposes, we need to expose the component instance
            // Since we can't expose the instance directly in a function component,
            // we'll expose the relevant methods
            attachToGlobalForTesting({
                drake: drakeRef.current as DrakeWithEmit,
                drop: onDrop,
                setupDragula,
                destroyDragula,
            });
        }
    }, [attachToGlobalForTesting, onDrop, setupDragula, destroyDragula]);

    /* no UI/component to render! */
    return null;
});

export default MediaDragAndDropPresentation;
